import { useEffect, useRef, useState } from 'react'

import { t, Translation } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { navIcons } from '../../nav-icons.js'
import { getInitialsFromName } from '../../views/portal/utils/format.js'
import './portal-global-search.css'

interface PersonResult {
    id: number
    name: string
    job_title: string | null
    avatar_url: string | null
}

// Workspace shortcuts (label + target path) are supplied by the app shell from
// the canonical nav config, already permission-filtered, so search labels and
// gating never drift from the rest of the app. Icons are mapped by path.
export interface WorkspaceShortcut {
    label: Translation
    url: string
}

export interface PortalGlobalSearchProps {
    isOpen: boolean
    lang: Language
    shortcuts: WorkspaceShortcut[]
    onClose: () => void
    onNavigate: (path: string) => void
}

export const PortalGlobalSearch = ({
    isOpen,
    lang,
    shortcuts,
    onClose,
    onNavigate,
}: PortalGlobalSearchProps) => {
    const [query, setQuery] = useState('')
    const [people, setPeople] = useState<PersonResult[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [cursorIndex, setCursorIndex] = useState(-1)
    const inputRef = useRef<HTMLInputElement>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const tS = t.portal.globalSearch

    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setQuery('')
            setPeople([])
            setCursorIndex(-1)
            setTimeout(() => inputRef.current?.focus(), 50)
        }
    }, [isOpen])

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)

        if (query.trim().length < 2) {
            setPeople([])
            return
        }

        debounceRef.current = setTimeout(() => {
            setIsLoading(true)
            fetch(`/api/portal/search?q=${encodeURIComponent(query.trim())}&limit=5`)
                .then(async (res) => {
                    if (res.ok) {
                        const body = (await res.json()) as {
                            data: { people: PersonResult[]; workspace: unknown[] }
                        }
                        setPeople(body.data.people)
                    }
                })
                .catch((err: unknown) => {
                    console.error('[PortalGlobalSearch] Search error:', err)
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }, 250)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [query])

    if (!isOpen) return null

    // Build flat list for keyboard navigation
    const q = query.trim().toLowerCase()

    const filteredShortcuts = q
        ? shortcuts.filter((s) => s.label(lang).toLowerCase().includes(q))
        : shortcuts

    const totalItems = people.length + filteredShortcuts.length

    const handleSelect = (type: 'person' | 'shortcut', index: number) => {
        if (type === 'person') {
            onNavigate(`/people/${people[index].id}`)
        } else {
            onNavigate(filteredShortcuts[index].url)
        }
        onClose()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const next = Math.min(cursorIndex + 1, totalItems - 1)
            setCursorIndex(next)
            itemRefs.current[next]?.scrollIntoView({ block: 'nearest' })
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prev = Math.max(cursorIndex - 1, -1)
            setCursorIndex(prev)
            if (prev >= 0) itemRefs.current[prev]?.scrollIntoView({ block: 'nearest' })
        } else if (e.key === 'Enter' && cursorIndex >= 0 && cursorIndex < totalItems) {
            e.preventDefault()
            if (cursorIndex < people.length) {
                handleSelect('person', cursorIndex)
            } else {
                handleSelect('shortcut', cursorIndex - people.length)
            }
        } else if (e.key === 'Escape') {
            onClose()
        }
    }

    let flatIdx = 0

    return (
        <div className="portal-search-overlay" onClick={onClose}>
            <div
                className="portal-search-overlay__modal"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                {/* Search input */}
                <div className="portal-search-overlay__header">
                    <svg
                        className="portal-search-overlay__icon"
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M7.333 12.667A5.333 5.333 0 107.333 2a5.333 5.333 0 000 10.667zM14 14l-2.9-2.9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        className="portal-search-overlay__input"
                        placeholder={tS.placeholder(lang)}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value)
                            setCursorIndex(-1)
                        }}
                    />
                    <kbd className="portal-search-overlay__kbd">Esc</kbd>
                </div>

                <div className="portal-search-overlay__body">
                    {/* People results */}
                    {people.length > 0 && (
                        <div className="portal-search-overlay__section">
                            <h3 className="portal-search-overlay__section-title">
                                {tS.people(lang)}
                            </h3>
                            {people.map((person, i) => {
                                const idx = flatIdx
                                flatIdx++
                                return (
                                    <button
                                        key={person.id}
                                        ref={(el) => {
                                            itemRefs.current[idx] = el
                                        }}
                                        type="button"
                                        className={`portal-search-overlay__item${cursorIndex === idx ? ' is-active' : ''}`}
                                        onClick={() => handleSelect('person', i)}
                                        onMouseEnter={() => setCursorIndex(idx)}
                                    >
                                        <div className="portal-search-overlay__avatar">
                                            {person.avatar_url ? (
                                                <img
                                                    src={person.avatar_url}
                                                    alt={person.name}
                                                    className="portal-search-overlay__avatar-img"
                                                />
                                            ) : (
                                                getInitialsFromName(person.name)
                                            )}
                                        </div>
                                        <div className="portal-search-overlay__item-info">
                                            <span className="portal-search-overlay__item-name">
                                                {person.name}
                                            </span>
                                            {person.job_title && (
                                                <span className="portal-search-overlay__item-desc">
                                                    {person.job_title}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    {/* Loading */}
                    {isLoading && q.length >= 2 && (
                        <div className="portal-search-overlay__loading">{tS.searching(lang)}</div>
                    )}

                    {/* No results */}
                    {!isLoading && q.length >= 2 && people.length === 0 && (
                        <div className="portal-search-overlay__empty">{tS.noResults(lang)}</div>
                    )}

                    {/* Workspace shortcuts */}
                    {filteredShortcuts.length > 0 && (
                        <div className="portal-search-overlay__section">
                            <h3 className="portal-search-overlay__section-title">
                                {tS.workspace(lang)}
                            </h3>
                            {filteredShortcuts.map((shortcut, i) => {
                                const idx = flatIdx
                                flatIdx++
                                return (
                                    <button
                                        key={shortcut.url}
                                        ref={(el) => {
                                            itemRefs.current[idx] = el
                                        }}
                                        type="button"
                                        className={`portal-search-overlay__item${cursorIndex === idx ? ' is-active' : ''}`}
                                        onClick={() => handleSelect('shortcut', i)}
                                        onMouseEnter={() => setCursorIndex(idx)}
                                    >
                                        <span className="portal-search-overlay__shortcut-icon">
                                            {navIcons[shortcut.url]}
                                        </span>
                                        <span className="portal-search-overlay__item-name">
                                            {shortcut.label(lang)}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Footer hint */}
                <div className="portal-search-overlay__footer">
                    <span>
                        <kbd>↑</kbd> <kbd>↓</kbd> {tS.navigate(lang)}
                    </span>
                    <span>
                        <kbd>↵</kbd> {tS.select(lang)}
                    </span>
                    <span>
                        <kbd>esc</kbd> {tS.close(lang)}
                    </span>
                </div>
            </div>
        </div>
    )
}
